<template>

    <div class="px-6 py-4 bg-minplan-beige -mb-4">
        <page-title :title="$t('documents.replace')"></page-title>
    </div>

    <jet-form-section @submitted="replaceDocument()">

        <template #form>

            <form-card :title="$t('documents.document')" :description="$t('documents.documentDescription')">

                <div class="col-span-6">
                    <label for="name" class="block text-sm font-medium text-gray-700">{{ $t('documents.name') }} <RequiredMark /></label>
                    <input type="text" name="name" id="name" v-model="form.name" class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300" autofocus />
                    <jet-input-error :message="form.errors.name" class="mt-2" />
                </div>

                <div class="col-span-6" v-if="$page.props.document_digital_sign_mode === 'On'">
                    <label class="block text-sm font-medium text-gray-700">{{ $t('documents.digitalSignature') }}</label>
                    <div class="mt-1 flex items-center space-x-4">
                        <label class="inline-flex items-center"><input type="radio" value="Yes" v-model="form.digital_signature" /> <span class="ml-2">{{ $t('actions.yes') }}</span></label>
                        <label class="inline-flex items-center ml-4"><input type="radio" value="No" v-model="form.digital_signature" /> <span class="ml-2">{{ $t('actions.no') }}</span></label>
                    </div>
                </div>

                <div class="col-span-6">

                    <label class="block text-sm font-medium text-gray-700">
                        {{ $t("documents.file") }} <RequiredMark />
                    </label>
                    <FileDropzone @file="onDropFile">
                        <div class="mt-1 flex items-center">

                            <div class="mt-2 w-full" v-show="fileUploaded">
                                <span class="block">{{fileUploaded}}</span>
                            </div>

                            <jet-secondary-button class="mt-2 mr-2" type="button" @click.prevent="selectNewFile"
                                                    v-if="!fileUploaded">{{ $t("actions.chooseFile") }}
                            </jet-secondary-button>
                            <jet-secondary-button class="mt-2 mr-2" type="button" @click.prevent="clearFileInput"
                                                    v-if="fileUploaded">{{ $t("actions.removeFile") }}
                            </jet-secondary-button>

                            <input type="file" class="hidden" ref="file" :accept="form.digital_signature === 'Yes' ? '.pdf' : allowedMimesAccept"
                                    @change="updateFileUploaded"/>
                        </div>
                    </FileDropzone>
                    <p class="my-3 text-sm text-gray-400">
                        <span v-if="form.digital_signature === 'Yes'">PDF (max. {{ signedMaxSizeInMb }} MB)</span>
                        <span v-else>{{ $t("documents.fileInfo", { size: maxSizeInMb, extensions: documentMimes.toUpperCase().replace(/,/g, ' / ') }) }}</span>
                    </p>
                    <jet-input-error :message="form.errors.file" class="mt-2" />
                </div>

                <div class="col-span-6">
                    <label for="comment" class="block text-sm font-medium text-gray-700">{{ $t('documents.comment') }}</label>
                    <div class="flex">
                        <textarea type="textarea" name="comment" id="comment" v-model="form.comment" class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300" />
                    </div>
                    <jet-input-error :message="form.errors.comment" class="mt-2" />
                </div>

            </form-card>

        </template>

        <template #actions>
            <div class="flex justify-end my-4 mx-6">

                <button @click="cancel" type="button" class="inline-block uppercase font-semibold text-xs tracking-widest">{{ $t('actions.cancel') }}</button>

                <primary-button :class="{ 'opacity-25': form.processing }" :disabled="form.processing" class="ml-3 ">
                    <span v-if="form.processing" class="flex items-center gap-1">
                        <svg class="animate-spin h-3 w-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                        {{ $t('actions.saving') }}
                    </span>
                    <span v-else>{{ $t("actions.save") }}</span>
                </primary-button>

            </div>
        </template>

    </jet-form-section>

    <teleport to="body">
        <digital-signature-config 
            v-if="showSignConfigModal"
            class="digital-signature-config-modal-root"
            :buyers="buyers"
            :pdf-base64="pdfBase64"
            :file-name="form.name || fileUploaded"
            :auth-user-id="auth_user_id"
            @cancel="showSignConfigModal = false"
            @done="finalizeDigitalSignature"
        />
    </teleport>

</template>


<script>
import { defineComponent, ref } from "vue";
import MainLayout from "@/Layouts/MainLayout";
import PrimaryButton from "@/Jetstream/PrimaryButton";
import JetSecondaryButton from "@/Jetstream/SecondaryButton";
import JetFormSection from "@/Jetstream/FormSection";
import JetInput from "@/Jetstream/Input";
import JetInputError from "@/Jetstream/InputError";
import JetLabel from "@/Jetstream/Label";
import PageTitle from "@/Shared/Components/PageTitle";
import { useForm, usePage } from "@inertiajs/inertia-vue3";
import FormCard from "@/Shared/Components/FormCard";
import RequiredMark from "@/Shared/Components/RequiredMark";
import DigitalSignatureConfig from "@/Shared/Components/Documents/Modals/DigitalSignatureConfig.vue";
import FileDropzone from "@/Shared/Components/FileDropzone.vue";

export default defineComponent({
    // `max_signed_document_upload_size`: Hotfix 08.10.2026, siehe Create.vue.
    props: ['document', 'buyers', 'auth_user_id', 'max_signed_document_upload_size'],
    inheritAttrs: false,
    layout: MainLayout,
    emits: ["closeModal"],
    components: {
        PageTitle,
        PrimaryButton,
        JetFormSection,
        JetInput,
        JetInputError,
        JetLabel,
        JetSecondaryButton,
        FormCard,
        RequiredMark,
        DigitalSignatureConfig,
        FileDropzone,
    },
    computed: {
        // Hotfix 08.10.2026: Grenze fuer zu signierende Dateien, siehe Create.vue.
        signedMaxBytes() {
            return this.max_signed_document_upload_size || 10 * 1024 * 1024;
        },
        signedMaxSizeInMb() {
            return Math.round(this.signedMaxBytes / 1024 / 1024);
        },
        maxSizeInMb() {
            const bytes = this.$page.props.branding?.max_document_upload_size || (20 * 1024 * 1024);
            return Math.round(bytes / 1024 / 1024);
        },
        documentMimes() {
            return this.$page.props.branding?.document_file_upload_mimes || 'pdf,docx,xlsx';
        },
        allowedMimesAccept() {
            return this.documentMimes.split(',').map(m => `.${m.trim().toLowerCase()}`).join(',');
        }
    },
    methods: {
        cancel() {
            this.$emit("closeModal");
        },
        replaceDocument() {

            if (this.$refs.file && this.$refs.file.files[0]) {
                const fsize = this.$refs.file.files[0].size;
                const maxBytes = this.$page.props.branding?.max_document_upload_size || (20 * 1024 * 1024);

                if (fsize > maxBytes) {
                    this.$notify({
                        group: "error",
                        title: "actions.error",
                        text: "documents.fileTooBig"
                    });
                    return;
                }

                const file = this.$refs.file.files[0];
                const fileExt = file.name.split('.').pop().toLowerCase();
                const mimes = this.documentMimes.split(',').map(m => m.trim().toLowerCase());

                if (this.form.digital_signature === 'Yes' && fileExt !== 'pdf') {
                    this.$notify({ group: "error", title: "actions.error", text: `Digital signature requires PDF.` });
                    return;
                }

                if (!mimes.includes(fileExt)) {
                    this.$notify({
                        group: "error",
                        title: "actions.error",
                        text: `Ungültiger Dateityp. Erlaubt sind: ${this.documentMimes.toUpperCase()}`
                    });
                    return;
                }

                this.form.file = this.$refs.file.files[0];

                if (this.form.digital_signature === 'Yes' && fsize > this.signedMaxBytes) {
                    this.$notify({
                        group: "error",
                        title: "actions.error",
                        text: this.$t("documents.signedFileTooBig", { size: this.signedMaxSizeInMb }),
                    });
                    return;
                }

                if (this.form.digital_signature === 'Yes') {
                    this.loadPdfBase64();
                    this.showSignConfigModal = true;
                    return;
                }
            } else if (this.form.digital_signature === 'Yes') {
                this.form.digital_signature = 'No';
            }

            this.submitDocument();
        },
        submitDocument() {
            this.form.post(this.document.replaceLink, {
                preserveScroll: true,
                onSuccess: () => this.$notify({
                    group: "success",
                    title: 'actions.success',
                    text: 'documents.updated'
                })
            });
        },
        loadPdfBase64() {
            const file = this.$refs.file.files[0];
            const reader = new FileReader();
            reader.onload = () => {
                const dataUrl = reader.result;
                if (typeof dataUrl === 'string') {
                    this.pdfBase64 = dataUrl.split(',')[1];
                    this.pdfLoaded = true;
                }
            };
            reader.readAsDataURL(file);
        },
        b64toFile(b64Data, contentType='', sliceSize=512) {
            const byteCharacters = atob(b64Data);
            const byteArrays = [];
            for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
                const slice = byteCharacters.slice(offset, offset + sliceSize);
                const byteNumbers = new Array(slice.length);
                for (let i = 0; i < slice.length; i++) byteNumbers[i] = slice.charCodeAt(i);
                byteArrays.push(new Uint8Array(byteNumbers));
            }
            return new File(byteArrays, this.form.name + ".pdf", { type: contentType });
        },
        finalizeDigitalSignature(payload) {
            this.showSignConfigModal = false;
            this.form.digital_sign_json = payload.digital_sign_json;
            if (payload.pdf_base64_signed) {
                this.form.file = this.b64toFile(payload.pdf_base64_signed, 'application/pdf');
            }
            this.submitDocument();
        },
        selectNewFile() {
            this.$refs.file.click();
        },
        clearFileInput() {
            if (this.$refs.file?.value) {
                this.$refs.file.value = null;
                this.fileUploaded = null;
            }
        },
        updateFileUploaded(){
            const file = this.$refs.file.files[0];

            if (!file) return;

            this.fileUploaded = file.name;
        },
        onDropFile(file) {
            const dt = new DataTransfer();
            dt.items.add(file);
            this.$refs.file.files = dt.files;
            this.$refs.file.dispatchEvent(new Event("change", { bubbles: true }));
        }
    },
    setup(props) {
        const fileUploaded = ref(null);
        const { props: pageProps } = usePage();
        const dsMode = pageProps.value.document_digital_sign_mode;

        const form = useForm({
            name: props.document.name,
            comment: props.document.comment,
            file: null,
            digital_signature: "No",
            digital_sign_json: null
        });

        return { form, fileUploaded };
    },
    data() {
        return {
            showSignConfigModal: false,
            pdfLoaded: false,
            pdfBase64: null,
        }
    }
});
</script>

<style scoped></style>
